import Image from '@tiptap/extension-image';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import { TableKit } from '@tiptap/extension-table';
import { EditorContent, useEditor, useEditorState, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Bold,
  Heading2,
  Heading3,
  Highlighter,
  ImagePlus,
  Italic,
  Link2,
  List,
  ListOrdered,
  Minus,
  Pilcrow,
  Quote,
  Redo2,
  Strikethrough,
  Subscript as SubIcon,
  Superscript as SupIcon,
  Table as TableIcon,
  Underline,
  Undo2,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { toast } from 'sonner';

import { mensajeDe } from '@/api/errors';
import { subirArchivo, validarImagen, type DestinoSubida } from '@/lib/archivos';
import { cn } from '@/lib/utils';

import {
  Alineacion,
  aEditor,
  ColorTexto,
  COLORES_TEXTO,
  desdeEditor,
  Resaltado,
  RESALTADOS,
  type Alineacion as TipoAlineacion,
} from './extensiones';

function Boton({
  icono: Icono,
  etiqueta,
  activo = false,
  onClick,
  deshabilitado = false,
}: {
  icono: LucideIcon;
  etiqueta: string;
  activo?: boolean;
  onClick: () => void;
  deshabilitado?: boolean;
}) {
  return (
    <button
      type="button"
      title={etiqueta}
      aria-label={etiqueta}
      aria-pressed={activo}
      disabled={deshabilitado}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={cn(
        'grid h-8 w-8 place-items-center rounded-md text-texto-2 hover:bg-[var(--pista)] disabled:opacity-40',
        activo && 'bg-[var(--pista)] text-azul-texto',
      )}
    >
      <Icono size={16} aria-hidden />
    </button>
  );
}

function Separador() {
  return <span className="mx-1 h-6 w-px bg-[var(--separador)]" aria-hidden />;
}

function Muestras({
  colores,
  onElegir,
  onQuitar,
  etiqueta,
}: {
  colores: Record<string, string>;
  onElegir: (c: string) => void;
  onQuitar: () => void;
  etiqueta: string;
}) {
  return (
    <div className="flex items-center gap-1" role="group" aria-label={etiqueta}>
      {Object.entries(colores).map(([k, hex]) => (
        <button
          key={k}
          type="button"
          title={`${etiqueta}: ${k}`}
          aria-label={`${etiqueta} ${k}`}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onElegir(k)}
          className="h-5 w-5 rounded-full border border-[var(--separador)]"
          style={{ background: hex }}
        />
      ))}
      <button
        type="button"
        title={`Quitar ${etiqueta.toLowerCase()}`}
        onMouseDown={(e) => e.preventDefault()}
        onClick={onQuitar}
        className="px-1 text-xs text-texto-3 hover:text-texto"
      >
        ×
      </button>
    </div>
  );
}

function Barra({
  editor,
  destinoImagen,
  extra,
}: {
  editor: Editor;
  destinoImagen?: DestinoSubida;
  extra?: ReactNode;
}) {
  const [subiendo, setSubiendo] = useState(false);
  const archivo = useRef<HTMLInputElement>(null);
  const e = useEditorState({
    editor,
    selector: ({ editor: ed }) => ({
      bold: ed.isActive('bold'),
      italic: ed.isActive('italic'),
      underline: ed.isActive('underline'),
      strike: ed.isActive('strike'),
      sub: ed.isActive('subscript'),
      sup: ed.isActive('superscript'),
      h2: ed.isActive('heading', { level: 2 }),
      h3: ed.isActive('heading', { level: 3 }),
      ul: ed.isActive('bulletList'),
      ol: ed.isActive('orderedList'),
      quote: ed.isActive('blockquote'),
      link: ed.isActive('link'),
      table: ed.isActive('table'),
      align: (ed.getAttributes('paragraph').alinear ??
        ed.getAttributes('heading').alinear ??
        'left') as string,
      undo: ed.can().undo(),
      redo: ed.can().redo(),
    }),
  });
  const c = () => editor.chain().focus();
  const alinear = (a: TipoAlineacion) => c().alinear(a).run();
  const enlace = () => {
    const actual = (editor.getAttributes('link').href as string | undefined) ?? '';
    const url = window.prompt('URL del enlace (vacío para quitarlo)', actual);
    if (url === null) return;
    if (url.trim() === '') c().extendMarkRange('link').unsetLink().run();
    else c().extendMarkRange('link').setLink({ href: url.trim() }).run();
  };
  const imagen = async (file: File | undefined) => {
    if (!file || !destinoImagen) return;
    const err = validarImagen(file);
    if (err) return void toast.error(err);
    setSubiendo(true);
    try {
      const r = await subirArchivo(file, destinoImagen);
      c()
        .setImage({ src: r.url ?? r.path, alt: '' })
        .run();
    } catch (x) {
      toast.error(mensajeDe(x));
    } finally {
      setSubiendo(false);
    }
  };

  return (
    <div className="sticky top-0 z-10 flex flex-wrap items-center gap-0.5 border-b border-[var(--separador)] bg-[var(--fondo-panel,inherit)] p-1">
      <Boton
        icono={Undo2}
        etiqueta="Deshacer"
        deshabilitado={!e.undo}
        onClick={() => c().undo().run()}
      />
      <Boton
        icono={Redo2}
        etiqueta="Rehacer"
        deshabilitado={!e.redo}
        onClick={() => c().redo().run()}
      />
      <Separador />
      <Boton icono={Pilcrow} etiqueta="Párrafo" onClick={() => c().setParagraph().run()} />
      <Boton
        icono={Heading2}
        etiqueta="Título"
        activo={e.h2}
        onClick={() => c().toggleHeading({ level: 2 }).run()}
      />
      <Boton
        icono={Heading3}
        etiqueta="Subtítulo"
        activo={e.h3}
        onClick={() => c().toggleHeading({ level: 3 }).run()}
      />
      <Separador />
      <Boton
        icono={Bold}
        etiqueta="Negrita"
        activo={e.bold}
        onClick={() => c().toggleBold().run()}
      />
      <Boton
        icono={Italic}
        etiqueta="Cursiva"
        activo={e.italic}
        onClick={() => c().toggleItalic().run()}
      />
      <Boton
        icono={Underline}
        etiqueta="Subrayado"
        activo={e.underline}
        onClick={() => c().toggleUnderline().run()}
      />
      <Boton
        icono={Strikethrough}
        etiqueta="Tachado"
        activo={e.strike}
        onClick={() => c().toggleStrike().run()}
      />
      <Boton
        icono={SubIcon}
        etiqueta="Subíndice"
        activo={e.sub}
        onClick={() => c().toggleSubscript().run()}
      />
      <Boton
        icono={SupIcon}
        etiqueta="Superíndice"
        activo={e.sup}
        onClick={() => c().toggleSuperscript().run()}
      />
      <Separador />
      <Boton
        icono={AlignLeft}
        etiqueta="Alinear a la izquierda"
        activo={e.align === 'left'}
        onClick={() => alinear('left')}
      />
      <Boton
        icono={AlignCenter}
        etiqueta="Centrar"
        activo={e.align === 'center'}
        onClick={() => alinear('center')}
      />
      <Boton
        icono={AlignRight}
        etiqueta="Alinear a la derecha"
        activo={e.align === 'right'}
        onClick={() => alinear('right')}
      />
      <Boton
        icono={AlignJustify}
        etiqueta="Justificar"
        activo={e.align === 'justify'}
        onClick={() => alinear('justify')}
      />
      <Separador />
      <Boton
        icono={List}
        etiqueta="Lista"
        activo={e.ul}
        onClick={() => c().toggleBulletList().run()}
      />
      <Boton
        icono={ListOrdered}
        etiqueta="Lista numerada"
        activo={e.ol}
        onClick={() => c().toggleOrderedList().run()}
      />
      <Boton
        icono={Quote}
        etiqueta="Cita"
        activo={e.quote}
        onClick={() => c().toggleBlockquote().run()}
      />
      <Boton icono={Minus} etiqueta="Salto de línea" onClick={() => c().setHardBreak().run()} />
      <Boton icono={Link2} etiqueta="Enlace" activo={e.link} onClick={enlace} />
      <Boton
        icono={TableIcon}
        etiqueta="Insertar tabla"
        onClick={() => c().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
      />
      {destinoImagen && (
        <>
          <Boton
            icono={ImagePlus}
            etiqueta={subiendo ? 'Subiendo imagen…' : 'Insertar imagen'}
            deshabilitado={subiendo}
            onClick={() => archivo.current?.click()}
          />
          <input
            ref={archivo}
            type="file"
            className="sr-only"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={(ev) => {
              void imagen(ev.target.files?.[0]);
              ev.target.value = '';
            }}
          />
        </>
      )}
      <Separador />
      <Muestras
        etiqueta="Color"
        colores={COLORES_TEXTO}
        onElegir={(k) => c().ponerColor(k).run()}
        onQuitar={() => c().quitarColor().run()}
      />
      <Separador />
      <Highlighter size={14} className="text-texto-3" aria-hidden />
      <Muestras
        etiqueta="Resaltado"
        colores={RESALTADOS}
        onElegir={(k) => c().resaltar(k).run()}
        onQuitar={() => c().quitarResaltado().run()}
      />
      {e.table && (
        <div className="flex w-full flex-wrap gap-1 border-t border-[var(--separador)] pt-1 text-xs">
          {(
            [
              ['+ fila', () => c().addRowAfter().run()],
              ['− fila', () => c().deleteRow().run()],
              ['+ columna', () => c().addColumnAfter().run()],
              ['− columna', () => c().deleteColumn().run()],
              ['Unir celdas', () => c().mergeCells().run()],
              ['Separar celdas', () => c().splitCell().run()],
              ['Encabezado', () => c().toggleHeaderRow().run()],
              ['Borrar tabla', () => c().deleteTable().run()],
            ] as const
          ).map(([t, fn]) => (
            <button
              key={t}
              type="button"
              className="btn-cs btn-vidrio px-2 py-0.5 text-xs"
              onMouseDown={(ev) => ev.preventDefault()}
              onClick={fn}
            >
              {t}
            </button>
          ))}
        </div>
      )}
      {extra}
    </div>
  );
}

/**
 * Editor enriquecido (TipTap v3, solo extensiones MIT). Valor y cambios en HTML. Las imágenes se
 * suben al CDN (`destinoImagen`) y se guardan con ruta relativa: `cdnBase` las convierte para
 * mostrarlas. Alineación, color y resaltado salen como clases (`ta-*`, `tc-*`, `hl-*`), que es lo
 * único que deja pasar el limpiador de la API.
 */
export function EditorRico({
  valor,
  onCambio,
  destinoImagen,
  cdnBase = null,
  deshabilitado = false,
  etiqueta = 'Contenido',
}: {
  valor: string;
  onCambio: (html: string) => void;
  destinoImagen?: DestinoSubida;
  cdnBase?: string | null;
  deshabilitado?: boolean;
  etiqueta?: string;
}) {
  const avisar = useRef(onCambio);
  useEffect(() => {
    avisar.current = onCambio;
  }, [onCambio]);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        code: false,
        codeBlock: false,
        link: { openOnClick: false },
      }),
      Subscript,
      Superscript,
      TableKit.configure({ table: { resizable: false } }),
      Image.configure({ inline: false }),
      Alineacion,
      ColorTexto,
      Resaltado,
    ],
    content: aEditor(valor, cdnBase),
    editable: !deshabilitado,
    immediatelyRender: false,
    editorProps: {
      attributes: {
        class: 'contenido-libro min-h-48 px-4 py-3 outline-none',
        'aria-label': etiqueta,
        role: 'textbox',
        'aria-multiline': 'true',
      },
    },
    onUpdate: ({ editor: ed }) => avisar.current(desdeEditor(ed.getHTML(), cdnBase)),
  });
  useEffect(() => {
    editor?.setEditable(!deshabilitado);
  }, [editor, deshabilitado]);

  if (!editor) return <div className="input-vidrio min-h-48" />;
  return (
    <div className="input-vidrio overflow-hidden p-0">
      {!deshabilitado && <Barra editor={editor} destinoImagen={destinoImagen} />}
      <EditorContent editor={editor} />
    </div>
  );
}
